import { usePreviewImage } from '@euler/app/components/media/ImageViewerContext';
import { MediaUploadStatusOverlay } from '@euler/app/components/media/UploadStatusOverlay';
import { Typography } from '@euler/app/components/typography';
import { useMediaCaptureWithAnnotation } from '@euler/app/flows/media/functions/useMediaCaptureWithAnnotation';
import { useTakePhoto } from '@euler/app/flows/media/functions/useTakePhoto';
import { TaskImageWatermark } from '@euler/app/flows/task/components/ImageWatermark';
import { SiteIcon } from '@euler/app/flows/task/components/SiteIcon';
import { SiteInspectionManager } from '@euler/app/flows/task/functions/task-detail/inspection/SiteInspectionManager';
import {
  mediaUploadEventTypeToStatus,
  MediaUploadTaskEvent,
} from '@euler/app/flows/task/functions/task-detail/MediaFileUploadController';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskHeaderMetrics } from '@euler/app/flows/task/functions/useTaskHeaderMetrics';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { Img } from '@euler/components/image/AdvancedImage';
import { HStack } from '@euler/components/layout/Stack';
import { useAppLoading } from '@euler/components/loading/Provider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  AbnormalLevel,
  ImageAnnotationScene,
  SiteInspectionType,
} from '@euler/model/enum';
import { PreInspection } from '@euler/model/task-detail';
import { isNotNull, showAlert } from '@euler/utils';
import { useCreation } from '@euler/utils/hooks';
import { MotiView } from 'moti';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  FlatList,
  StyleProp,
  StyleSheet,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { ImageStyle } from 'react-native-fast-image';
import { Fill } from 'react-slot-fill';
import { Subject } from 'rxjs';
import { useTheme } from 'styled-components';
import { useDashboardInspectionFlow } from '../functions/useDashboardInspectionFlow';
import { TroubleLightItem } from './TroubleLightCell';
import { TroubleLightInspectionModal } from './TroubleLightInspectionModal';

type DashboardInspectionProps = {
  noShadow?: boolean;
  style?: StyleProp<ViewStyle>;
  imageContainerStyle?: StyleProp<ViewStyle>;
  touchableImageContainerStyle?: StyleProp<ViewStyle>;
  shadeStyle?: StyleProp<ViewStyle>;
  troubleLightsContainerStyle?: StyleProp<ViewStyle>;
  troubleLightsLabelStyle?: StyleProp<TextStyle>;
  troubleLightsPlaceholderTextStyle?: StyleProp<TextStyle>;
  troubleLightsPlaceholderIconColor?: string;
  retakeButtonStyle?: StyleProp<ViewStyle>;
  retakeButtonSize?: number;
};

export const WiredDashboardInspection = memo(
  ({
    width,
    height,
    ...props
  }: {
    width: number;
    height: number;
  } & DashboardInspectionProps) => {
    const appLoading = useAppLoading();

    const { detail, taskManager } = useTaskContext();
    const [uploadEvent, setUploadEvent] = useState<MediaUploadTaskEvent>();
    const [localFileUri, setLocalFileUri] = useState<string>();
    const [remoteFileUri, setRemoteFileUri] = useState<string>();
    const [isModalVisible, setIsModalVisible] = useState(false);

    const {
      isWatermarkRequired,
      sites,
      onSiteCheckChange,
      endDashboardInspection,
    } = useDashboardInspectionFlow();

    const items = useMemo(() => {
      if (!sites) return [];
      return sites
        .map(site => {
          const manager =
            taskManager.preInspectionManager.getSiteInspectionManager(site.id);
          if (!manager) return null;
          return {
            item: {
              siteId: site.id,
              siteCode: site.code,
              name: site.name,
              icon: site.iconUrl,
            },
            manager,
          };
        })
        .filter(isNotNull);
    }, [sites, taskManager.preInspectionManager]);

    const upload = useCallback(
      (fileUri: string) => {
        taskManager.uploadController.enqueue({
          type: 'image',
          contentType: 'image/jpeg',
          localFileUri: fileUri,
          parameters: { blurhash: true },
          listener: async event => {
            setUploadEvent(event);
            if (event.type === 'finished') {
              try {
                setRemoteFileUri(event.result.url);
                setUploadEvent(undefined);
                await taskManager.updateBasicInfo({
                  dashboardImgUrl: event.result.url,
                });
              } catch (e) {
                void showAlert('出错了', (e as Error).message);
              }
            }
          },
        });
      },
      [taskManager],
    );

    const takePhoto = useTakePhoto({
      quality: 95,
    });
    const takePhotoWithAnnotation = useMediaCaptureWithAnnotation({
      scene: ImageAnnotationScene.Dashboard,
      photoOnly: true,
      multiple: false,
      maxMediaCountToSelect: 1,
    });

    const onTakePhoto = useCallback(() => {
      if (isWatermarkRequired) {
        takePhotoWithAnnotation({
          watermark: (scale?: number) => (
            <TaskImageWatermark
              licensePlateNo={detail.basicInfo.licensePlateNo}
              title={'仪表拍照'}
              author={taskManager.currentUser.user.name}
              store={taskManager.currentUser.store.name}
              type="pre-inspection"
              scale={scale}
            />
          ),
          handleImageAnnotationResults: async results => {
            const result = results[0];
            if (result == null) return;
            setLocalFileUri(result.imageUri);
            upload(result.imageUri);
          },
        });
      } else {
        takePhoto(res => {
          setLocalFileUri(res.uri);
          upload(res.uri);
        });
      }
    }, [
      detail.basicInfo.licensePlateNo,
      isWatermarkRequired,
      takePhoto,
      takePhotoWithAnnotation,
      taskManager.currentUser.store.name,
      taskManager.currentUser.user.name,
      upload,
    ]);

    const previewDashboardImage = usePreviewImage();
    const onPreview = useCallback(
      (dashboardImageUrl: string) => {
        previewDashboardImage({ imageUrls: [dashboardImageUrl] });
      },
      [previewDashboardImage],
    );

    const onRetry = useCallback(() => {
      if (!localFileUri) return;
      upload(localFileUri);
    }, [localFileUri, upload]);

    const onInspectPress = useCallback(() => {
      setIsModalVisible(true);
    }, []);

    useEffect(() => () => endDashboardInspection(), [endDashboardInspection]);

    const onDismiss = useCallback(() => {
      setIsModalVisible(false);
      taskManager.preInspectionManager.resetSiteInspectionBundle('dashboard');
    }, [taskManager.preInspectionManager]);

    const forceRefreshSignal$ = useCreation(() => new Subject<void>(), []);

    const onCommit = useCallback(async () => {
      try {
        appLoading.show();
        await taskManager.preInspectionManager.commitSiteInspectionBundle(
          'dashboard',
          SiteInspectionType.Dashboard,
        );
        appLoading.hide();
        onDismiss();
        forceRefreshSignal$.next();
      } catch (e) {
        appLoading.hide();
        void showAlert('出错了', (e as Error).message);
      }
    }, [
      appLoading,
      forceRefreshSignal$,
      onDismiss,
      taskManager.preInspectionManager,
    ]);

    return (
      <>
        <DashboardInspection
          forceRefreshSignal$={forceRefreshSignal$}
          dashboardImageUrl={
            uploadEvent != null &&
            uploadEvent.type !== 'abort' &&
            uploadEvent.type !== 'finished' &&
            localFileUri
              ? localFileUri
              : remoteFileUri ?? detail.preInspection.dashboardImgUrl
          }
          imageWidth={width}
          imageHeight={height}
          uploadEvent={uploadEvent}
          items={items}
          onTakePhoto={onTakePhoto}
          onPreview={onPreview}
          onRetry={onRetry}
          onInspectPress={onInspectPress}
          {...props}
        />
        <Fill name="task:bottom-panel">
          <TroubleLightInspectionModal
            items={items}
            isVisible={isModalVisible}
            onSiteCheckChange={onSiteCheckChange}
            onDismiss={onDismiss}
            onCommit={onCommit}
          />
        </Fill>
      </>
    );
  },
);

export const DashboardInspection = memo(
  ({
    dashboardImageUrl,
    noShadow,
    imageWidth,
    imageHeight,
    retakeButtonSize,
    style,
    shadeStyle,
    imageContainerStyle,
    touchableImageContainerStyle,
    troubleLightsContainerStyle,
    troubleLightsLabelStyle,
    retakeButtonStyle,
    troubleLightsPlaceholderTextStyle,
    troubleLightsPlaceholderIconColor,
    uploadEvent,
    forceRefreshSignal$,
    items,
    onTakePhoto,
    onPreview,
    onRetry,
    onInspectPress,
  }: {
    imageWidth: number;
    imageHeight: number;
    dashboardImageUrl: string | undefined;
    onTakePhoto?: () => void;
    uploadEvent?: MediaUploadTaskEvent;
    forceRefreshSignal$: Subject<void>;
    items: {
      item: TroubleLightItem;
      manager: SiteInspectionManager<PreInspection>;
    }[];
    onPreview?: (dashboardImageUrl: string) => void;
    onRetry?: () => void;
    onInspectPress?: () => void;
  } & DashboardInspectionProps) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const { offset } = useTaskHeaderMetrics();

    retakeButtonSize = retakeButtonSize ?? rpx(44);

    const onPress = useCallback(() => {
      if (dashboardImageUrl) {
        onPreview?.(dashboardImageUrl);
      } else {
        onTakePhoto?.();
      }
    }, [dashboardImageUrl, onPreview, onTakePhoto]);

    const status =
      uploadEvent != null
        ? mediaUploadEventTypeToStatus(uploadEvent)
        : undefined;

    const progress =
      uploadEvent?.type === 'progress' || uploadEvent?.type === 'compressing'
        ? uploadEvent.progress
        : uploadEvent?.type === 'begin'
        ? 0
        : undefined;

    const shadow =
      dashboardImageUrl != null
        ? '2px 2px 10px rgba(0, 0, 0, 0.3)'
        : '0px 0px 2px rgba(0, 0, 0, 0.2)';

    return (
      <View style={style}>
        {/* shade view */}
        <View
          css={`
            background-color: #fff;
            position: absolute;
            left: 0;
            right: 0;
            top: ${offset}px;
            bottom: 0;
          `}
          style={shadeStyle}
        />

        {/* content container */}
        <View
          css={`
            elevation: 1;
            width: ${imageWidth}px;
            height: ${imageHeight}px;
            align-self: center;
            ${noShadow ? '' : `box-shadow: ${shadow}`}
          `}
          style={imageContainerStyle}
        >
          {/* image view or capture button container */}
          <TouchableOpacity
            css={`
              flex: 1;
              background-color: #eee;
              border-radius: ${rpx(15)}px;
              overflow: hidden;
            `}
            onPress={onPress}
            activeOpacity={0.95}
            style={touchableImageContainerStyle}
          >
            {dashboardImageUrl == null ? (
              <Center
                css={`
                  flex: 1;
                `}
              >
                <Icon name="camera" size={rpx(44)} color="#ccc" />
                <Typography.Label
                  light
                  color="#666"
                  size={rpx(16)}
                  lineHeight={rpx(40)}
                >
                  点击拍摄仪表盘照片
                </Typography.Label>
              </Center>
            ) : (
              <Img
                uri={dashboardImageUrl}
                css={`
                  flex: 1;
                `}
              />
            )}

            {/* image upload status overlay */}
            {status != null && (
              <MediaUploadStatusOverlay
                status={status}
                progressViewSize={rpx(64)}
                progress={progress}
                style={StyleSheet.absoluteFill}
                onRetry={onRetry}
              />
            )}

            {/* image retake button */}
            {dashboardImageUrl != null && status == null ? (
              <TouchableOpacity
                onPress={onTakePhoto}
                css={`
                  width: ${retakeButtonSize}px;
                  height: ${retakeButtonSize}px;
                  align-items: center;
                  justify-content: center;
                  position: absolute;
                  right: 0;
                  bottom: 0;
                `}
                style={retakeButtonStyle}
              >
                <Icon
                  name="camera"
                  size={retakeButtonSize * (32 / 44)}
                  color="#ddd"
                />
              </TouchableOpacity>
            ) : null}
          </TouchableOpacity>
        </View>

        {/* trouble lights container */}
        <TouchableOpacity
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: ${rpx(10)}px ${rpx(15)}px;
            padding-right: ${rpx(3)}px;
            margin-top: ${rpx(5)}px;
          `}
          style={troubleLightsContainerStyle}
          onPress={onInspectPress}
        >
          <View
            css={`
              flex-direction: row;
              align-items: center;
            `}
          >
            <Icon
              name="lightbulb-on"
              size={rpx(24)}
              color={StatusColors.Warn}
            />
            <Typography.Label
              size={rpx(19)}
              light
              css={`
                margin-left: ${rpx(4)}px;
              `}
              style={troubleLightsLabelStyle}
            >
              仪表故障灯
            </Typography.Label>
          </View>
          <DefectiveSiteIconList
            items={items}
            forceRefreshSignal$={forceRefreshSignal$}
            onPress={onInspectPress}
            placeholderTextStyle={troubleLightsPlaceholderTextStyle}
            placeholderIconColor={troubleLightsPlaceholderIconColor}
            css={`
              flex: 1;
              margin-left: ${rpx(15)}px;
            `}
          />
          <Icon
            name="chevron-right"
            size={rpx(32)}
            color={theme.components.table.item.detailDisclosureIconColor}
          />
        </TouchableOpacity>
      </View>
    );
  },
);

const DefectiveSiteIconList = memo(
  ({
    forceRefreshSignal$,
    items,
    style,
    placeholderIconColor,
    placeholderTextStyle,
    onPress,
  }: {
    forceRefreshSignal$: Subject<void>;
    items: {
      item: TroubleLightItem;
      manager: SiteInspectionManager<PreInspection>;
    }[];
    style?: StyleProp<ViewStyle>;
    placeholderIconColor?: string;
    placeholderTextStyle?: StyleProp<TextStyle>;
    onPress?: () => void;
  }) => {
    const [forceUpdate, setForceUpdate] = useState(0);
    const { rpx } = useResponsiveContext();

    useEffect(() => {
      const sub = forceRefreshSignal$.subscribe(() => {
        setForceUpdate(x => x + 1);
      });
      return () => sub.unsubscribe();
    }, [forceRefreshSignal$]);

    const data = useMemo(
      () =>
        [...items].reverse().filter(x => {
          return x.manager.staged$
            .getValue()
            ?.items.some(t => t.abnormalLevel !== AbnormalLevel.Fine);
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [items, forceUpdate],
    );

    if (data.length === 0) {
      return (
        <HStack style={style} justifyContent="flex-end">
          <Icon
            size={rpx(14)}
            name="information-outline"
            color={placeholderIconColor ?? '#999'}
          />
          <Typography.Label
            size={rpx(14)}
            color="#999"
            light
            lineHeight={rpx(18)}
            css={`
              text-align: right;
              margin-left: 1px;
            `}
            style={placeholderTextStyle}
          >
            点击记录仪表故障灯状态
          </Typography.Label>
        </HStack>
      );
    }
    return (
      <FlatList
        horizontal
        showsHorizontalScrollIndicator={false}
        data={data}
        keyExtractor={x => x.item.siteCode}
        renderItem={({ item }) => (
          <DefectiveSiteIconCell
            item={item.item}
            onPress={onPress}
            css={`
              margin: 0 ${rpx(2)}px;
            `}
          />
        )}
        style={[
          style,
          {
            transform: [{ scaleX: -1 }],
          },
        ]}
      />
    );
  },
);

const DefectiveSiteIconCell = memo(
  ({
    item,
    style,
    onPress,
  }: {
    item: TroubleLightItem;
    style?: StyleProp<ImageStyle>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <MotiView
        from={{
          opacity: 0,
          translateX: rpx(55),
        }}
        animate={{ opacity: 1, translateX: 0 }}
        transition={{ delay: 100 }}
        style={style}
      >
        <TouchableOpacity onPress={onPress}>
          <SiteIcon
            url={item.icon}
            type={
              item.icon
                ? item.icon.includes('.svg')
                  ? 'svg'
                  : 'image'
                : undefined
            }
            size={rpx(16)}
            color="#888"
          />
        </TouchableOpacity>
      </MotiView>
    );
  },
);
